import { isClientRuntime } from '@skin-seoul/env-utils';

import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { recaptchaAdapterRegistry } from './recaptchaAdapterRegistry';
import { recaptchaRegistry } from './recaptchaRegistry';
import { PLATFORM_HEADER, RECAPTCHA_TOKEN_HEADER } from './utils/constants';
import { initializeDefaultEndpoints } from './recaptchaConfig';

let endpointsInitialized = false;

function ensureEndpointsInitialized(): void {
  if (endpointsInitialized) {
    return;
  }
  initializeDefaultEndpoints();
  endpointsInitialized = true;
}

/**
 * Attach reCAPTCHA interceptor to Axios instance
 *
 * - Skips token injection on the server (React Native counts as a client)
 * - Only injects token for registered endpoints
 * - Uses adapter from registry (supports web/mobile platforms)
 */
export function attachRecaptchaInterceptor(instance: AxiosInstance): void {
  ensureEndpointsInitialized();

  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // isClientRuntime, not typeof window: React Native leaves `window` undefined,
      // which skipped this interceptor entirely and sent every protected native call untokened.
      if (!isClientRuntime()) {
        return config;
      }

      const adapter = recaptchaAdapterRegistry.getAdapter();
      if (!adapter?.isAvailable()) {
        return config;
      }

      const url = config.url || '';
      const path = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0];

      const headers = config.headers ? Object.fromEntries(Object.entries(config.headers)) : undefined;
      const action = recaptchaRegistry.getActionForPath(path, config.method, { body: config.data, headers });
      if (!action) {
        return config;
      }

      const token = await adapter.getToken(action);
      if (!token) {
        return config;
      }

      config.headers[RECAPTCHA_TOKEN_HEADER] = token;
      config.headers[PLATFORM_HEADER] = adapter.os;
      return config;
    },
    (error) => Promise.reject(error),
  );
}
